import type { Entry, Fixture, Round } from '@h2h/shared';
import { ArrowRight, Check, Clock, X } from '../../components/ui/icons';
import { RiderAvatar } from '../../components/play/cycling-ui/RiderAvatar';
import { ConfidenceBadge } from '../../components/rounds/shared';
import { buildDuelVMs, shortRider, type DuelVM } from '../../lib/cyclingRound';
import { parseRiderName } from '../../lib/riderDisplay';
import { signedPoints } from '../../lib/format';
import { cn } from '../../lib/cn';
import { kickoffLabel } from './helpers';

/** Single-duel view when the player hasn't filled their full ladder yet. */
export function CyclingSingleDuelFallback({
  fixture,
  fixtures,
  entry,
  round,
  canEnter,
  stageLive,
  onPicks
}: {
  fixture: Fixture;
  fixtures: Fixture[];
  entry: Entry | null;
  round: Round | null;
  canEnter: boolean;
  stageLive: boolean;
  onPicks: () => void;
}) {
  const map = new Map(fixtures.map((f) => [f.fixtureId, f]));
  const vm = entry ? (buildDuelVMs(entry, map).find((d) => d.duelId === fixture.fixtureId) ?? null) : null;

  const settled = fixture.status === 'SETTLED' || fixture.status === 'VOID';
  const live = fixture.status === 'LIVE';
  const voided = fixture.status === 'VOID' || fixture.duelResult?.outcome === 'void';
  const winnerSide = fixture.duelResult?.winner ?? null;
  const aName = fixture.riderA?.riderName ?? fixture.homeTeam;
  const bName = fixture.riderB?.riderName ?? fixture.awayTeam;
  const winnerName = winnerSide === 'A' ? aName : winnerSide === 'B' ? bName : null;
  const roundOpen = round?.status === 'OPEN' || round?.status === 'LOCKING';

  return (
    <>
      <DuelHeroCard
        fixture={fixture}
        aName={aName}
        bName={bName}
        vm={vm}
        settled={settled}
        live={live}
        voided={voided}
        winnerName={winnerName}
        winnerSide={winnerSide}
      />

      <div className="mt-5 flex items-center justify-between px-0.5">
        <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-ink3">Your call</span>
        {vm ? <ConfidenceBadge weight={vm.confidence} active={vm.status === 'settled' && !!vm.correct} /> : null}
      </div>

      {vm ? (
        <YourCallCard vm={vm} stageLive={stageLive} />
      ) : (
        <NoCallCard entry={entry} canEnter={canEnter} roundOpen={roundOpen} onPicks={onPicks} />
      )}
    </>
  );
}

function DuelHeroCard({
  fixture,
  aName,
  bName,
  vm,
  settled,
  live,
  voided,
  winnerName,
  winnerSide
}: {
  fixture: Fixture;
  aName: string;
  bName: string;
  vm: DuelVM | null;
  settled: boolean;
  live: boolean;
  voided: boolean;
  winnerName: string | null;
  winnerSide: 'A' | 'B' | null;
}) {
  const statusLine = voided
    ? 'Void — neither rider gave a comparable finish'
    : settled && winnerName
      ? `${shortRider(winnerName)} finished ahead`
      : live
        ? 'Stage rolling — result pending'
        : kickoffLabel(fixture.kickoffAt);

  return (
    <div className="mt-3 overflow-hidden rounded-[18px] border border-black/10 bg-wash px-4 py-4 shadow-tile">
      <div className="flex items-start justify-between gap-3">
        <RiderSide
          name={aName}
          team={fixture.riderA?.teamName}
          nationality={fixture.riderA?.nationality}
          backed={vm?.pick === 'A'}
          won={settled && winnerSide === 'A'}
          lost={settled && winnerSide === 'B'}
        />
        <span className="mt-3 text-[13px] font-bold text-ink4">v</span>
        <RiderSide
          name={bName}
          team={fixture.riderB?.teamName}
          nationality={fixture.riderB?.nationality}
          backed={vm?.pick === 'B'}
          won={settled && winnerSide === 'B'}
          lost={settled && winnerSide === 'A'}
        />
      </div>

      <div
        className={cn(
          'mt-4 rounded-[12px] px-3 py-2.5 text-center text-[12px] font-semibold',
          live ? 'bg-[rgba(227,168,43,0.12)] text-[#B5740A]' : 'bg-white text-ink2'
        )}
      >
        {statusLine}
      </div>
    </div>
  );
}

function RiderSide({
  name,
  team,
  nationality,
  backed,
  won,
  lost
}: {
  name: string;
  team?: string | null;
  nationality?: string | null;
  backed: boolean;
  won: boolean;
  lost: boolean;
}) {
  const { surname, firstName } = parseRiderName(name);

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center">
      <div
        className={cn(
          'rounded-full ring-2 ring-offset-2 ring-offset-wash',
          won ? 'ring-accent' : backed ? 'ring-accent/50' : lost ? 'ring-black/10 opacity-70' : 'ring-black/10'
        )}
      >
        <RiderAvatar name={name} nationality={nationality} size="lg" />
      </div>
      <div className="min-w-0 w-full">
        <div className="truncate text-[13px] tracking-[-0.01em] text-ink">
          <span className={cn('font-extrabold', backed && 'text-accent-ink')}>{surname.toUpperCase()}</span>
          {firstName ? <span className="font-medium text-ink3"> {firstName}</span> : null}
        </div>
        {team ? <div className="mt-0.5 truncate text-[11px] font-semibold text-ink3">{team}</div> : null}
        {backed ? (
          <div className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.06em] text-accent-ink">Your pick</div>
        ) : won ? (
          <div className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.06em] text-accent-ink">Winner</div>
        ) : null}
      </div>
    </div>
  );
}

function YourCallCard({ vm, stageLive }: { vm: DuelVM; stageLive: boolean }) {
  const won = vm.status === 'settled' && !!vm.correct;
  const lost = vm.status === 'settled' && vm.correct === false;

  const shell =
    vm.status === 'void'
      ? 'border-black/10 bg-[#F3F3F6]'
      : won
        ? 'border-accent/30 bg-accent-wash'
        : lost
          ? 'border-miss/35 bg-[rgba(210,84,79,0.09)]'
          : stageLive
            ? 'border-[#E3A82B]/35 bg-[rgba(227,168,43,0.10)]'
            : 'border-black/10 bg-white';

  return (
    <div className={cn('mt-2.5 overflow-hidden rounded-[16px] border p-[14px] shadow-tile', shell)}>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'grid h-8 w-8 shrink-0 place-items-center rounded-full',
            vm.status === 'upcoming' && 'border-2 border-black/15 bg-white',
            vm.status === 'void' && 'bg-[#E8E8ED] text-ink3',
            won && 'bg-accent text-white',
            lost && 'bg-miss text-white'
          )}
        >
          {won && <Check width={15} height={15} strokeWidth={3.2} />}
          {lost && <X width={15} height={15} strokeWidth={3.2} />}
          {vm.status === 'upcoming' && stageLive && <Clock width={14} height={14} strokeWidth={2.2} className="text-[#B5740A]" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-extrabold tracking-snug text-ink">
            You backed {shortRider(vm.pickedName)} over {shortRider(vm.otherName)}
          </div>
          <div className="mt-1.5 text-[12px] font-semibold text-ink2">
            {vm.status === 'void'
              ? 'No points — duel voided'
              : won
                ? 'Your pick won this duel'
                : lost
                  ? 'Your pick lost this duel'
                  : stageLive
                    ? 'Locked in — waiting for the finish'
                    : `Staked at ×${vm.confidence} on your ladder`}
          </div>
        </div>
        <div className="text-right">
          {vm.points != null ? (
            <>
              <div className={cn('num text-[22px] font-extrabold leading-none tracking-[-0.02em]', won ? 'text-accent-ink' : lost ? 'text-miss' : 'text-ink')}>
                {signedPoints(vm.points)}
              </div>
              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink3">points</div>
            </>
          ) : (
            <>
              <div className="num text-[18px] font-extrabold leading-none text-ink2">×{vm.confidence}</div>
              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink3">stake</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NoCallCard({
  entry,
  canEnter,
  roundOpen,
  onPicks
}: {
  entry: Entry | null;
  canEnter: boolean;
  roundOpen: boolean;
  onPicks: () => void;
}) {
  const message = entry
    ? 'This duel isn’t on your card — finish calling all six on Compete.'
    : canEnter
      ? 'You haven’t called the duels for this stage yet.'
      : roundOpen
        ? 'Calls have closed for this stage.'
        : 'You didn’t enter this stage.';

  return (
    <div className="mt-2.5 rounded-[16px] border border-dashed border-black/12 bg-[#FAFAFB] px-4 py-6 text-center">
      <p className="text-[13px] font-medium leading-[1.45] text-ink2">{message}</p>
      {!entry && canEnter && (
        <button
          type="button"
          onClick={onPicks}
          className="tap mt-4 inline-flex items-center justify-center gap-1.5 rounded-[14px] bg-night px-5 py-2.5 text-[13px] font-extrabold text-white shadow-dark"
        >
          Call the duels
          <ArrowRight width={13} height={13} strokeWidth={2.8} />
        </button>
      )}
    </div>
  );
}
